import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, logout } from '../utils/api';
import toast from 'react-hot-toast';
import { FaArrowRight, FaChartLine, FaBox, FaClipboardList, FaEye, FaPlay, FaCrown, FaCheck, FaImages, FaShoppingCart, FaTruck, FaGlobe, FaEnvelope } from 'react-icons/fa';

// Demo data - completely separate from production data
const demoStats = {
  totalOrders: 247,
  totalDispatched: 189,
  totalAdvanceReceived: 892450,
  totalBalancePending: 345600,
  lowStockCount: 8
};

const demoInventory = [
  {
    id: 'demo-1',
    modelName: 'Traditional Ganesh Idol',
    modelNumber: 'G001',
    size: '12 Inch',
    price: 2500,
    currentStock: 15,
    initialStock: 20,
    sold: 45,
    photo: '/gpm.png'
  },
  {
    id: 'demo-2',
    modelName: 'Decorative Ganesh Murti',
    modelNumber: 'G002',
    size: '8 Inch',
    price: 1800,
    currentStock: 8,
    initialStock: 12,
    sold: 28,
    photo: '/gpm.png'
  },
  {
    id: 'demo-3',
    modelName: 'Premium Ganesh Statue',
    modelNumber: 'G003',
    size: '18 Inch',
    price: 4500,
    currentStock: 5,
    initialStock: 8,
    sold: 12,
    photo: '/gpm.png'
  }
];

// Initial demo orders (used for state initialization)
const initialDemoOrders = [
  {
    id: 'demo-order-1',
    customerName: 'Rajesh Kumar',
    phone: '9876543210',
    items: [{ modelName: 'Traditional Ganesh Idol', quantity: 2, price: 2500 }],
    status: 'dispatched',
    advanceAmount: 4000,
    totalAmount: 5000,
    orderDate: '2024-01-15',
    balanceReceived: true
  },
  {
    id: 'demo-order-2',
    customerName: 'Priya Sharma',
    phone: '8765432109',
    items: [{ modelName: 'Decorative Ganesh Murti', quantity: 1, price: 1800 }],
    status: 'pending',
    advanceAmount: 1440,
    totalAmount: 1800,
    orderDate: '2024-01-14',
    balanceReceived: false
  },
  {
    id: 'demo-order-3',
    customerName: 'Amit Singh',
    phone: '7654321098',
    items: [{ modelName: 'Premium Ganesh Statue', quantity: 1, price: 4500 }],
    status: 'dispatched',
    advanceAmount: 3600,
    totalAmount: 4500,
    orderDate: '2024-01-13',
    balanceReceived: false
  }
];

// Initial demo inventory (used for state initialization)
const initialDemoInventory = [
  {
    id: 'demo-1',
    modelName: 'Traditional Ganesh Idol',
    modelNumber: 'G001',
    size: '12 Inch',
    price: 2500,
    currentStock: 15,
    initialStock: 20,
    sold: 45,
    photo: '/gpm.png'
  },
  {
    id: 'demo-2',
    modelName: 'Decorative Ganesh Murti',
    modelNumber: 'G002',
    size: '8 Inch',
    price: 1800,
    currentStock: 8,
    initialStock: 12,
    sold: 28,
    photo: '/gpm.png'
  },
  {
    id: 'demo-3',
    modelName: 'Premium Ganesh Statue',
    modelNumber: 'G003',
    size: '18 Inch',
    price: 4500,
    currentStock: 5,
    initialStock: 8,
    sold: 12,
    photo: '/gpm.png'
  }
];

// Extended demo inventory for gallery view
const extendedDemoInventory = [
  ...demoInventory,
  {
    id: 'demo-4',
    modelName: 'Mini Ganesh Idol',
    modelNumber: 'G004',
    size: '6 Inch',
    price: 1200,
    currentStock: 25,
    initialStock: 30,
    sold: 89,
    photo: '/gpm.png',
    category: 'small'
  },
  {
    id: 'demo-5',
    modelName: 'Large Ganesh Statue',
    modelNumber: 'G005',
    size: '24 Inch',
    price: 8500,
    currentStock: 3,
    initialStock: 5,
    sold: 7,
    photo: '/gpm.png',
    category: 'large'
  },
  {
    id: 'demo-6',
    modelName: 'Designer Ganesh Murti',
    modelNumber: 'G006',
    size: '10 Inch',
    price: 3200,
    currentStock: 12,
    initialStock: 15,
    sold: 34,
    photo: '/gpm.png',
    category: 'medium'
  }
];

const DemoPage = () => {
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedItem, setSelectedItem] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    productInterest: '',
    quantity: 1,
    message: ''
  });
  const [orderConfirmation, setOrderConfirmation] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [retailForm, setRetailForm] = useState({
    customerName: 'Demo Customer',
    phone: '9876543210',
    date: new Date().toISOString().split('T')[0],
    category: 'medium',
    model: 'demo-3',
    advance: 4500
  });
  const [wholesaleForm, setWholesaleForm] = useState({
    customerName: 'Wholesale Buyer',
    phone: '8765432109',
    date: new Date().toISOString().split('T')[0],
    items: [
      { category: 'small', model: 'demo-4', quantity: 5, advance: 6000 },
      { category: 'medium', model: 'demo-6', quantity: 3, advance: 4800 }
    ]
  });
  const [demoOrders, setDemoOrders] = useState([
    {
      id: 'demo-order-1',
      customerName: 'Rajesh Kumar',
      phone: '9876543210',
      items: [{ modelName: 'Traditional Ganesh Idol', quantity: 2, price: 2500 }],
      status: 'dispatched',
      advanceAmount: 4000,
      totalAmount: 5000,
      orderDate: '2024-01-15',
      balanceReceived: true
    },
    {
      id: 'demo-order-2',
      customerName: 'Priya Sharma',
      phone: '8765432109',
      items: [{ modelName: 'Decorative Ganesh Murti', quantity: 1, price: 1800 }],
      status: 'pending',
      advanceAmount: 1440,
      totalAmount: 1800,
      orderDate: '2024-01-14',
      balanceReceived: false
    },
    {
      id: 'demo-order-3',
      customerName: 'Amit Singh',
      phone: '7654321098',
      items: [{ modelName: 'Premium Ganesh Statue', quantity: 1, price: 4500 }],
      status: 'dispatched',
      advanceAmount: 3600,
      totalAmount: 4500,
      orderDate: '2024-01-13',
      balanceReceived: false
    }
  ]);
  const navigate = useNavigate();

  const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

  const sections = [
    { id: 'overview', name: 'Dashboard Overview', icon: FaChartLine, description: 'Business analytics & key metrics' },
    { id: 'gallery', name: 'Inventory Gallery', icon: FaImages, description: 'Visual inventory display' },
    { id: 'inventory', name: 'Inventory Management', icon: FaBox, description: 'Track stock, prices & models' },
    { id: 'retail-orders', name: 'Retail Orders', icon: FaShoppingCart, description: 'Create retail customer orders' },
    { id: 'wholesale-orders', name: 'Wholesale Orders', icon: FaTruck, description: 'Create bulk wholesale orders' },
    { id: 'orders', name: 'Order Processing', icon: FaClipboardList, description: 'Manage customer orders' }
  ];

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await getUser();
        setUser(userData);
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const handleContinueToPricing = () => {
    navigate('/pricing');
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!contactForm.name.trim() || !contactForm.phone.trim() || !contactForm.email.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate phone number
    if (!/^[6-9]\d{9}$/.test(contactForm.phone.trim())) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Store inquiry in localStorage for demo purposes
    const inquiry = {
      id: `inquiry-${Date.now()}`,
      ...contactForm,
      timestamp: new Date().toISOString(),
      status: 'received'
    };

    const existingInquiries = JSON.parse(localStorage.getItem('demoInquiries') || '[]');
    existingInquiries.push(inquiry);
    localStorage.setItem('demoInquiries', JSON.stringify(existingInquiries));

    // Reset form
    setContactForm({
      name: '',
      phone: '',
      email: '',
      productInterest: '',
      quantity: 1,
      message: ''
    });

    toast.success('Thank you for your inquiry! We\'ll get back to you soon.', {
      duration: 5000
    });
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">🎉 Welcome to Your Demo Dashboard!</h2>
        <p className="text-gray-600 mb-4">
          This is a preview of what you'll get with a subscription. Explore the features below!
        </p>
        <div className="flex items-center gap-2 text-sm text-blue-700">
          <FaEye className="text-blue-500" />
          <span>All data shown is for demonstration purposes</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{demoStats.totalOrders}</p>
            </div>
            <div className="text-3xl">📋</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Orders Dispatched</p>
              <p className="text-2xl font-bold text-blue-600">{demoStats.totalDispatched}</p>
            </div>
            <div className="text-3xl">🚚</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Advance Received</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(demoStats.totalAdvanceReceived)}</p>
            </div>
            <div className="text-3xl">💰</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Balance Pending</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(demoStats.totalBalancePending)}</p>
            </div>
            <div className="text-3xl">⏳</div>
          </div>
        </div>
      </div>

      {/* Inventory Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">📊 Inventory Summary</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200/50">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-blue-900 mb-1">Total Items</h4>
                <p className="text-3xl font-bold text-blue-700">{demoInventory.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📦</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200/50">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-green-900 mb-1">In Stock</h4>
                <p className="text-3xl font-bold text-green-700">
                  {demoInventory.filter(item => item.currentStock > 0).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">✅</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-xl border border-yellow-200/50">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-yellow-900 mb-1">Low Stock</h4>
                <p className="text-3xl font-bold text-yellow-700">{demoStats.lowStockCount}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">⚠️</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200/50">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-purple-900 mb-1">Total Value</h4>
                <p className="text-3xl font-bold text-purple-700">
                  {formatCurrency(demoInventory.reduce((sum, item) => sum + (item.price * item.currentStock), 0))}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💎</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInventory = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">📦 Inventory Management Demo</h2>
        <p className="text-gray-600">
          Manage your Ganesh idol inventory with detailed tracking, pricing, and stock management.
        </p>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Sample Inventory Items</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {demoInventory.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img className="h-10 w-10 rounded-lg object-cover" src={item.photo} alt={item.modelName} />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{item.modelName}</div>
                        <div className="text-sm text-gray-500">{item.modelNumber}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.size}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.price)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.currentStock}/{item.initialStock}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      item.currentStock > item.initialStock * 0.5
                        ? 'bg-green-100 text-green-800'
                        : item.currentStock > 0
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.currentStock > item.initialStock * 0.5 ? 'Good' : item.currentStock > 0 ? 'Low' : 'Out'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedItem(item)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete "${item.modelName}"?`)) {
                            // Remove item from demo inventory (in real app, this would call an API)
                            toast.success(`Item "${item.modelName}" deleted successfully!`, { duration: 3000 });
                          }
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Interactive Demo */}
      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h4 className="text-lg font-semibold text-blue-900 mb-3">🎮 Try These Features:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h5 className="font-medium text-gray-900 mb-2">📊 Stock Analysis</h5>
            <p className="text-sm text-gray-600 mb-3">
              Hover over stock levels to see trends and reorder suggestions.
            </p>
            <button className="text-blue-600 text-sm hover:underline">
              Click any item above →
            </button>
          </div>

          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <h5 className="font-medium text-gray-900 mb-2">🔍 Advanced Search</h5>
            <p className="text-sm text-gray-600 mb-3">
              Filter by price, size, stock status, and more.
            </p>
            <span className="text-blue-600 text-sm">Available in full version</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGallery = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">🖼️ Inventory Gallery Demo</h2>
        <p className="text-gray-600">
          Visual inventory management with photo gallery - the way customers browse your products.
        </p>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {extendedDemoInventory.map((item) => {
          const stockPercentage = item.initialStock > 0 ? (item.currentStock / item.initialStock) * 100 : 0;

          let gradientClass = '';
          let ringClass = '';
          let extraClasses = '';

          if (item.currentStock === 0) {
            gradientClass = 'bg-gradient-to-br from-red-100 to-red-200 border-red-400';
            ringClass = 'ring-4 ring-red-400';
          } else if (stockPercentage > 60) {
            gradientClass = 'bg-gradient-to-br from-green-50 to-green-100 border-green-200';
          } else if (stockPercentage >= 40) {
            gradientClass = 'bg-gradient-to-br from-yellow-50 to-orange-100 border-orange-200';
          } else {
            gradientClass = 'bg-gradient-to-br from-red-50 to-red-100 border-red-200';
          }

          return (
            <div
              key={item.id}
              onClick={() => setSelectedItem(item)}
              className={`rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-200 hover:scale-105 border-2 ${gradientClass} ${ringClass} ${extraClasses}`}
            >
              {/* Image Section */}
              <div className="h-48 relative bg-white">
                <img
                  src={item.photo}
                  alt={item.modelName}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Stock Display Section */}
              <div className={`flex-1 flex flex-col justify-center items-center p-3 ${
                item.currentStock === 0
                  ? 'bg-gradient-to-br from-red-100 to-red-200'
                  : stockPercentage > 60
                    ? 'bg-gradient-to-br from-green-50 to-green-100'
                    : stockPercentage >= 40
                      ? 'bg-gradient-to-br from-yellow-50 to-orange-100'
                      : 'bg-gradient-to-br from-red-50 to-red-100'
              }`}>
                {/* Stock Status Badge */}
                <div className={`mb-2 px-3 py-1 rounded-full text-xs font-bold ${item.currentStock === 0
                  ? 'bg-red-600 text-white'
                  : item.currentStock < 5
                    ? 'bg-red-500 text-white'
                    : stockPercentage > 60
                      ? 'bg-green-600 text-white'
                      : stockPercentage >= 40
                        ? 'bg-orange-600 text-white'
                        : 'bg-red-600 text-white'
                }`}>
                  {item.currentStock === 0 ? 'OUT OF STOCK' : 'IN STOCK'}
                </div>

                {/* Large Stock Number */}
                <div className={`text-4xl font-black mb-1 ${item.currentStock === 0
                  ? 'text-red-600'
                  : item.currentStock < 5
                    ? 'text-red-500'
                    : stockPercentage > 60
                      ? 'text-green-600'
                      : stockPercentage >= 40
                        ? 'text-orange-600'
                        : 'text-red-600'
                }`}>
                  {item.currentStock}
                </div>

                {/* Stock Label */}
                <div className="text-sm text-gray-600 font-medium mb-2">
                  LEFT
                </div>

                {/* Model Info */}
                <div className="text-center">
                  <div className="text-sm font-bold text-gray-800 truncate">{item.modelName}</div>
                  <div className="text-base font-semibold text-gray-700">Model {item.modelNumber}</div>
                  <div className="text-lg font-bold text-green-600 mt-1">{formatCurrency(item.price)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gallery Features */}
      <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
        <h4 className="text-lg font-semibold text-purple-900 mb-3">🎨 Gallery Features:</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border border-purple-200">
            <h5 className="font-medium text-gray-900 mb-2">📸 Visual Display</h5>
            <p className="text-sm text-gray-600">
              Large product images with hover effects and zoom capabilities.
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-purple-200">
            <h5 className="font-medium text-gray-900 mb-2">📊 Stock Indicators</h5>
            <p className="text-sm text-gray-600">
              Color-coded stock status with clear availability information.
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-purple-200">
            <h5 className="font-medium text-gray-900 mb-2">🔍 Quick Details</h5>
            <p className="text-sm text-gray-600">
              Click any item to view detailed specifications and pricing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRetailOrderForm = () => {
    const selectedModel = extendedDemoInventory.find(item => item.id === retailForm.model);
    const totalValue = selectedModel ? selectedModel.price : 0;
    const balance = totalValue - retailForm.advance;

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">🛒 Retail Order Demo</h2>
          <p className="text-gray-600">
            Create retail orders for individual customers - perfect for direct sales and walk-ins.
          </p>
        </div>

        {/* Demo Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sample Retail Order</h3>

          <form className="space-y-6">
            {/* Customer Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={retailForm.customerName}
                  onChange={(e) => setRetailForm(prev => ({ ...prev, customerName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={retailForm.phone}
                  onChange={(e) => setRetailForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="10-digit number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Date
                </label>
                <input
                  type="date"
                  value={retailForm.date}
                  onChange={(e) => setRetailForm(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Item Selection */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={retailForm.category}
                    onChange={(e) => setRetailForm(prev => ({ ...prev, category: e.target.value, model: 'demo-3' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="small">Small (1-30)</option>
                    <option value="medium">Medium (31-60)</option>
                    <option value="large">Large (61-90)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <select
                    value={retailForm.model}
                    onChange={(e) => setRetailForm(prev => ({ ...prev, model: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {extendedDemoInventory.filter(item => item.category === retailForm.category).map(item => (
                      <option key={item.id} value={item.id}>
                        {item.modelName} - {formatCurrency(item.price)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Advance Payment
                  </label>
                  <input
                    type="number"
                    value={retailForm.advance}
                    onChange={(e) => setRetailForm(prev => ({ ...prev, advance: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    min="0"
                  />
                </div>
              </div>

              {/* Selected Item Display */}
              {selectedModel && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-4">
                    <img src={selectedModel.photo} alt={selectedModel.modelName} className="w-16 h-16 object-cover rounded" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{selectedModel.modelName}</h4>
                      <p className="text-sm text-gray-600">Model {selectedModel.modelNumber} • {selectedModel.size}</p>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(selectedModel.price)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-3">Order Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(totalValue)}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Advance Paid</p>
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(retailForm.advance)}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Balance Due</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(balance)}</p>
                </div>
              </div>
            </div>

            {/* Demo Action */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => {
                  const selectedModel = extendedDemoInventory.find(item => item.id === retailForm.model);
                  if (!selectedModel) {
                    toast.error('Please select a valid product');
                    return;
                  }

                  // Validate required fields
                  if (!retailForm.customerName.trim() || !retailForm.phone.trim()) {
                    toast.error('Please fill in customer name and phone number');
                    return;
                  }

                  // Generate demo order confirmation
                  const orderNumber = `DEMO-${Date.now().toString().slice(-6)}`;
                  const deliveryDate = new Date(retailForm.date);
                  deliveryDate.setDate(deliveryDate.getDate() + 7); // Add 7 days for delivery

                  const confirmation = {
                    orderNumber,
                    customerName: retailForm.customerName,
                    phone: retailForm.phone,
                    deliveryDate: retailForm.date,
                    estimatedDelivery: deliveryDate.toISOString().split('T')[0],
                    item: selectedModel,
                    totalAmount: selectedModel.price,
                    advancePaid: retailForm.advance,
                    balanceDue: selectedModel.price - retailForm.advance,
                    orderDate: new Date().toISOString().split('T')[0]
                  };

                  setOrderConfirmation(confirmation);
                  setShowOrderModal(true);

                  // Store in localStorage for demo persistence
                  const existingOrders = JSON.parse(localStorage.getItem('demoRetailOrders') || '[]');
                  existingOrders.push(confirmation);
                  localStorage.setItem('demoRetailOrders', JSON.stringify(existingOrders));

                  toast.success('Demo order created successfully!', {
                    duration: 3000
                  });
                }}
                className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              >
                🎯 Create Demo Retail Order
              </button>
            </div>
          </form>
        </div>

        {/* Retail Order Features */}
        <div className="bg-green-50 rounded-lg p-6 border border-green-200">
          <h4 className="text-lg font-semibold text-green-900 mb-3">🛒 Retail Order Features:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg border border-green-200">
              <h5 className="font-medium text-gray-900 mb-2">👤 Single Customer</h5>
              <p className="text-sm text-gray-600">
                Perfect for individual customers buying one item at a time.
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-green-200">
              <h5 className="font-medium text-gray-900 mb-2">💰 Full Payment</h5>
              <p className="text-sm text-gray-600">
                Typically full payment upfront, or advance + balance on delivery.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWholesaleOrderForm = () => {
    const calculateTotal = () => {
      return wholesaleForm.items.reduce((total, item) => {
        const model = extendedDemoInventory.find(m => m.id === item.model);
        return total + (model ? model.price * item.quantity : 0);
      }, 0);
    };

    const calculateTotalAdvance = () => {
      return wholesaleForm.items.reduce((total, item) => total + item.advance, 0);
    };

    const totalValue = calculateTotal();
    const totalAdvance = calculateTotalAdvance();
    const balance = totalValue - totalAdvance;

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">🚛 Wholesale Order Demo</h2>
          <p className="text-gray-600">
            Create bulk orders for wholesale customers - handle multiple items with quantity discounts.
          </p>
        </div>

        {/* Demo Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sample Wholesale Order</h3>

          <form className="space-y-6">
            {/* Customer Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={wholesaleForm.customerName}
                  onChange={(e) => setWholesaleForm(prev => ({ ...prev, customerName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={wholesaleForm.phone}
                  onChange={(e) => setWholesaleForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="10-digit number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Date
                </label>
                <input
                  type="date"
                  value={wholesaleForm.date}
                  onChange={(e) => setWholesaleForm(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Order Items */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Order Items</h4>
              {wholesaleForm.items.map((item, index) => {
                const model = extendedDemoInventory.find(m => m.id === item.model);
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <select
                          value={item.category}
                          onChange={(e) => {
                            const newItems = [...wholesaleForm.items];
                            newItems[index].category = e.target.value;
                            newItems[index].model = extendedDemoInventory.find(m => m.category === e.target.value)?.id || '';
                            setWholesaleForm(prev => ({ ...prev, items: newItems }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Model
                        </label>
                        <select
                          value={item.model}
                          onChange={(e) => {
                            const newItems = [...wholesaleForm.items];
                            newItems[index].model = e.target.value;
                            setWholesaleForm(prev => ({ ...prev, items: newItems }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {extendedDemoInventory.filter(m => m.category === item.category).map(m => (
                            <option key={m.id} value={m.id}>
                              {m.modelName} - {formatCurrency(m.price)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quantity
                        </label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const newItems = [...wholesaleForm.items];
                            newItems[index].quantity = parseInt(e.target.value) || 1;
                            setWholesaleForm(prev => ({ ...prev, items: newItems }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="1"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Advance
                        </label>
                        <input
                          type="number"
                          value={item.advance}
                          onChange={(e) => {
                            const newItems = [...wholesaleForm.items];
                            newItems[index].advance = parseFloat(e.target.value) || 0;
                            setWholesaleForm(prev => ({ ...prev, items: newItems }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Item Display */}
                    {model && (
                      <div className="mt-3 p-3 bg-white rounded border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <img src={model.photo} alt={model.modelName} className="w-12 h-12 object-cover rounded" />
                            <div>
                              <p className="font-medium text-gray-900">{model.modelName}</p>
                              <p className="text-sm text-gray-600">{formatCurrency(model.price)} × {item.quantity}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{formatCurrency(model.price * item.quantity)}</p>
                            <p className="text-sm text-blue-600">Advance: {formatCurrency(item.advance)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-3">Wholesale Order Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Grand Total</p>
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(totalValue)}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Total Advance</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(totalAdvance)}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Balance Due</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(balance)}</p>
                </div>
              </div>
            </div>

            {/* Demo Action */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => {
                  // Validate required fields
                  if (!wholesaleForm.customerName.trim() || !wholesaleForm.phone.trim()) {
                    toast.error('Please fill in customer name and phone number');
                    return;
                  }

                  // Check if at least one item is selected
                  if (wholesaleForm.items.every(item => !item.model)) {
                    toast.error('Please select at least one product');
                    return;
                  }

                  // Generate demo order confirmation
                  const orderNumber = `DEMO-W-${Date.now().toString().slice(-6)}`;
                  const deliveryDate = new Date(wholesaleForm.date);
                  deliveryDate.setDate(deliveryDate.getDate() + 14); // Add 14 days for wholesale delivery

                  const orderItems = wholesaleForm.items.filter(item => item.model).map(item => {
                    const model = extendedDemoInventory.find(m => m.id === item.model);
                    return {
                      model: model,
                      quantity: item.quantity,
                      advance: item.advance,
                      total: model ? model.price * item.quantity : 0
                    };
                  });

                  const totalAmount = orderItems.reduce((sum, item) => sum + item.total, 0);
                  const totalAdvance = orderItems.reduce((sum, item) => sum + item.advance, 0);

                  const confirmation = {
                    orderNumber,
                    customerName: wholesaleForm.customerName,
                    phone: wholesaleForm.phone,
                    deliveryDate: wholesaleForm.date,
                    estimatedDelivery: deliveryDate.toISOString().split('T')[0],
                    items: orderItems,
                    totalAmount,
                    advancePaid: totalAdvance,
                    balanceDue: totalAmount - totalAdvance,
                    orderDate: new Date().toISOString().split('T')[0],
                    type: 'wholesale'
                  };

                  setOrderConfirmation(confirmation);
                  setShowOrderModal(true);

                  // Store in localStorage for demo persistence
                  const existingOrders = JSON.parse(localStorage.getItem('demoWholesaleOrders') || '[]');
                  existingOrders.push(confirmation);
                  localStorage.setItem('demoWholesaleOrders', JSON.stringify(existingOrders));

                  toast.success('Demo wholesale order created successfully!', {
                    duration: 3000
                  });
                }}
                className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                📦 Create Demo Wholesale Order
              </button>
            </div>
          </form>
        </div>

        {/* Wholesale Order Features */}
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <h4 className="text-lg font-semibold text-blue-900 mb-3">🚛 Wholesale Order Features:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h5 className="font-medium text-gray-900 mb-2">📊 Bulk Ordering</h5>
              <p className="text-sm text-gray-600">
                Add multiple items and quantities for large orders.
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h5 className="font-medium text-gray-900 mb-2">💰 Flexible Payment</h5>
              <p className="text-sm text-gray-600">
                Distribute advance payments across multiple items.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderShopDemo = () => {
    // Sample shop data
    const shopStats = {
      totalProducts: 15,
      availableProducts: 13,
      totalOrders: 89,
      totalRevenue: 245000,
      avgOrderValue: 2750,
      recentOrders: 12
    };

    const shopInventory = [
      {
        id: 'shop-1',
        modelName: 'Traditional Ganesh Idol',
        modelNumber: 'G001',
        size: '12 Inch',
        price: 2500,
        category: 'medium',
        inStock: true,
        bestseller: true
      },
      {
        id: 'shop-2',
        modelName: 'Mini Ganesh Idol',
        modelNumber: 'G004',
        size: '6 Inch',
        price: 1200,
        category: 'small',
        inStock: true,
        featured: true
      },
      {
        id: 'shop-3',
        modelName: 'Designer Ganesh Murti',
        modelNumber: 'G006',
        size: '10 Inch',
        price: 3200,
        category: 'medium',
        inStock: true,
        bestseller: true
      },
      {
        id: 'shop-4',
        modelName: 'Large Ganesh Statue',
        modelNumber: 'G005',
        size: '24 Inch',
        price: 8500,
        category: 'large',
        inStock: true,
        featured: true
      }
    ];

    const shopOrders = [
      {
        id: 'shop-order-1',
        customerName: 'Priya Sharma',
        items: 'Traditional Ganesh Idol × 1',
        totalAmount: 2500,
        status: 'delivered',
        orderDate: '2024-01-15'
      },
      {
        id: 'shop-order-2',
        customerName: 'Rajesh Kumar',
        items: 'Mini Ganesh Idol × 2',
        totalAmount: 2400,
        status: 'shipped',
        orderDate: '2024-01-14'
      },
      {
        id: 'shop-order-3',
        customerName: 'Sneha Patel',
        items: 'Designer Ganesh Murti × 1',
        totalAmount: 3200,
        status: 'processing',
        orderDate: '2024-01-13'
      }
    ];

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">🌐 Public Shop - Your Revenue Engine</h2>
          <p className="text-gray-600">
            Your customer-facing online shop where people can buy Ganesh idols retail or wholesale. No login required!
          </p>
        </div>

        {/* Shop Access - Moved to Top */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-6 text-white">
          <h3 className="text-xl font-bold mb-3 text-center">🛍️ Visit Your Public Shop</h3>
          <p className="text-orange-100 mb-6 text-center">
            Experience your fully functional online storefront. Browse products, add to cart, and complete purchases just like real customers do.
          </p>

          <div className="flex justify-center">
            <button
              onClick={() => navigate('/demo-shop')}
              className="bg-white text-orange-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center gap-3 text-lg"
            >
              <FaGlobe className="text-2xl" />
              Browse Demo Shop
              <FaArrowRight />
            </button>
          </div>
        </div>

        {/* Shop Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <div className="text-2xl font-bold text-blue-600">{shopStats.totalProducts}</div>
            <div className="text-sm text-gray-600">Products Available</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <div className="text-2xl font-bold text-green-600">{shopStats.totalOrders}</div>
            <div className="text-sm text-gray-600">Orders Completed</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <div className="text-2xl font-bold text-purple-600">{formatCurrency(shopStats.totalRevenue)}</div>
            <div className="text-sm text-gray-600">Total Revenue</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <div className="text-2xl font-bold text-orange-600">{formatCurrency(shopStats.avgOrderValue)}</div>
            <div className="text-sm text-gray-600">Avg Order Value</div>
          </div>
        </div>

        {/* Featured Products */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">⭐ Featured Products in Your Shop</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {shopInventory.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="text-center mb-3">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-2 flex items-center justify-center">
                    <span className="text-2xl">🕉️</span>
                  </div>
                  {item.bestseller && (
                    <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full mb-2">
                      🏆 Bestseller
                    </span>
                  )}
                  {item.featured && !item.bestseller && (
                    <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mb-2">
                      ⭐ Featured
                    </span>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900 text-sm mb-1">{item.modelName}</h4>
                <p className="text-xs text-gray-600 mb-2">{item.size} • Model {item.modelNumber}</p>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-green-600">{formatCurrency(item.price)}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${item.inStock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.inStock ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Shop Orders */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📦 Recent Customer Orders from Shop</h3>
          <div className="space-y-3">
            {shopOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-blue-600">
                      {order.customerName.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{order.customerName}</p>
                    <p className="text-sm text-gray-600">{order.items}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatCurrency(order.totalAmount)}</p>
                  <p className="text-xs text-gray-500">{order.orderDate}</p>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                    order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                    order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {order.status === 'delivered' ? '✅ Delivered' :
                     order.status === 'shipped' ? '🚚 Shipped' : '⏳ Processing'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Shop Access */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-6 text-white">
          <h3 className="text-xl font-bold mb-3 text-center">🛍️ Visit Your Public Shop</h3>
          <p className="text-orange-100 mb-6 text-center">
            Experience your fully functional online storefront. Browse products, add to cart, and complete purchases just like real customers do.
          </p>

          <div className="flex justify-center">
            <button
              onClick={() => navigate('/demo-shop')}
              className="bg-white text-orange-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center gap-3 text-lg"
            >
              <FaGlobe className="text-2xl" />
              Browse Demo Shop
              <FaArrowRight />
            </button>
          </div>
        </div>

        {/* Shop Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Retail Shopping */}
          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
            <h4 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
              <FaShoppingCart className="text-green-600" />
              Retail Shopping
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <FaCheck className="text-green-500 text-xs" />
                Individual customers buy single items
              </li>
              <li className="flex items-center gap-2">
                <FaCheck className="text-green-500 text-xs" />
                Walk-in sales and online purchases
              </li>
              <li className="flex items-center gap-2">
                <FaCheck className="text-green-500 text-xs" />
                Full payment or advance + balance
              </li>
              <li className="flex items-center gap-2">
                <FaCheck className="text-green-500 text-xs" />
                Immediate delivery available
              </li>
            </ul>
          </div>

          {/* Wholesale Shopping */}
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h4 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <FaTruck className="text-blue-600" />
              Wholesale Shopping
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <FaCheck className="text-blue-500 text-xs" />
                Bulk orders for businesses/resellers
              </li>
              <li className="flex items-center gap-2">
                <FaCheck className="text-blue-500 text-xs" />
                Multiple items and quantities
              </li>
              <li className="flex items-center gap-2">
                <FaCheck className="text-blue-500 text-xs" />
                Flexible payment terms
              </li>
              <li className="flex items-center gap-2">
                <FaCheck className="text-blue-500 text-xs" />
                Scheduled delivery options
              </li>
            </ul>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
          <h4 className="text-lg font-semibold text-purple-900 mb-3">🔄 How Shop Orders Work</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="bg-white p-4 rounded-lg">
              <div className="text-2xl mb-2">🛒</div>
              <h5 className="font-medium text-gray-900 mb-1">Customer Orders</h5>
              <p className="text-sm text-gray-600">
                Customers place orders through your public shop
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <div className="text-2xl mb-2">📊</div>
              <h5 className="font-medium text-gray-900 mb-1">Auto-Import</h5>
              <p className="text-sm text-gray-600">
                Orders automatically appear in your management system
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <div className="text-2xl mb-2">🎯</div>
              <h5 className="font-medium text-gray-900 mb-1">Track & Fulfill</h5>
              <p className="text-sm text-gray-600">
                Manage production, shipping, and customer communication
              </p>
            </div>
          </div>
        </div>

        {/* Business Value */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg p-6 text-white">
          <h4 className="text-xl font-bold mb-3">💰 Your Shop = Your Revenue Stream</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-semibold mb-2">📈 Sales Channels</h5>
              <ul className="text-sm space-y-1 text-emerald-100">
                <li>• Online shop (24/7 sales)</li>
                <li>• Customer referrals</li>
                <li>• Wholesale partnerships</li>
                <li>• Seasonal festival demand</li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold mb-2">🎯 Management Benefits</h5>
              <ul className="text-sm space-y-1 text-emerald-100">
                <li>• Automated order tracking</li>
                <li>• Inventory synchronization</li>
                <li>• Customer history & insights</li>
                <li>• Payment & delivery management</li>
              </ul>
            </div>
          </div>


        </div>
      </div>
    );
  };

  const renderOrders = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">📋 Order Processing Demo</h2>
        <p className="text-gray-600">
          Streamline your order management with real-time tracking and customer communication.
        </p>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
        </div>

        <div className="divide-y divide-gray-200">
          {demoOrders.map((order) => (
            <div key={order.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{order.customerName}</h4>
                    <p className="text-sm text-gray-500">{order.phone}</p>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrency(order.totalAmount)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Advance: {formatCurrency(order.advanceAmount)}
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    order.status === 'dispatched'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {order.status === 'dispatched' ? '🚚 Dispatched' : '⏳ Pending'}
                  </span>

                  {order.status === 'pending' && (
                    <button
                      onClick={() => {
                        const updatedOrders = demoOrders.map(o =>
                          o.id === order.id ? { ...o, status: 'dispatched' } : o
                        );
                        setDemoOrders(updatedOrders);
                        localStorage.setItem('demoOrders', JSON.stringify(updatedOrders));
                        toast.success('Order marked as dispatched!', { duration: 3000 });
                      }}
                      className="bg-green-600 text-white px-3 py-1 text-sm font-medium rounded hover:bg-green-700 transition-colors"
                    >
                      Mark as Dispatched
                    </button>
                  )}

                  {order.status === 'dispatched' && !order.balanceReceived && (
                    <button
                      onClick={() => {
                        const updatedOrders = demoOrders.map(o =>
                          o.id === order.id ? { ...o, balanceReceived: true } : o
                        );
                        setDemoOrders(updatedOrders);
                        localStorage.setItem('demoOrders', JSON.stringify(updatedOrders));
                        toast.success('Balance payment marked as received!', { duration: 3000 });
                      }}
                      className="bg-blue-600 text-white px-3 py-1 text-sm font-medium rounded hover:bg-blue-700 transition-colors"
                    >
                      Mark Balance Received
                    </button>
                  )}

                  {order.balanceReceived && (
                    <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                      ✅ Completed
                    </span>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="mt-4">
                <div className="text-sm text-gray-600">
                  <strong>Items:</strong> {order.items.map(item =>
                    `${item.modelName} × ${item.quantity} (${formatCurrency(item.price * item.quantity)})`
                  ).join(', ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Workflow Demo */}
      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
        <h4 className="text-lg font-semibold text-green-900 mb-3">⚡ Order Workflow:</h4>
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
            <span className="text-green-800">Order Received</span>
          </div>
          <FaArrowRight className="text-green-500" />
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
            <span className="text-green-800">Advance Payment</span>
          </div>
          <FaArrowRight className="text-green-500" />
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
            <span className="text-green-800">Production</span>
          </div>
          <FaArrowRight className="text-green-500" />
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">4</div>
            <span className="text-green-800">Dispatch</span>
          </div>
          <FaArrowRight className="text-green-500" />
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">5</div>
            <span className="text-green-800">Balance Collection</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContactUs = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">📬 Contact Us</h2>
        <p className="text-gray-600">
          Have questions about our products? Need custom requirements? We're here to help!
        </p>
      </div>

      {/* Contact Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Send us your inquiry</h3>

        <form onSubmit={handleContactSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                required
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                required
                value={contactForm.phone}
                onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="9876543210"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              required
              value={contactForm.email}
              onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="your.email@example.com"
            />
          </div>

          {/* Product Interest */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Interest
              </label>
              <select
                value={contactForm.productInterest}
                onChange={(e) => setContactForm(prev => ({ ...prev, productInterest: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Select a product...</option>
                <option value="traditional-ganesh">Traditional Ganesh Idol</option>
                <option value="decorative-ganesh">Decorative Ganesh Murti</option>
                <option value="premium-ganesh">Premium Ganesh Statue</option>
                <option value="mini-ganesh">Mini Ganesh Idol</option>
                <option value="large-ganesh">Large Ganesh Statue</option>
                <option value="designer-ganesh">Designer Ganesh Murti</option>
                <option value="custom-order">Custom Order</option>
                <option value="bulk-wholesale">Bulk/Wholesale Inquiry</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity Needed
              </label>
              <input
                type="number"
                min="1"
                value={contactForm.quantity}
                onChange={(e) => setContactForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="1"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Message & Requirements
            </label>
            <textarea
              rows="4"
              value={contactForm.message}
              onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Please describe your requirements, special customizations, delivery preferences, or any questions you have..."
            />
          </div>

          {/* Demo Notice */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              🎭 <strong>Demo Mode:</strong> This is a demonstration contact form. Your inquiry will be stored locally and we'll show a confirmation message.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
            >
              📤 Send Inquiry
            </button>
          </div>
        </form>
      </div>

      {/* Contact Information */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">📞 Get in Touch</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-gray-900 mb-2">Business Hours</h5>
            <p className="text-sm text-gray-600">
              Monday - Saturday: 9:00 AM - 8:00 PM<br />
              Sunday: 10:00 AM - 6:00 PM
            </p>
          </div>

          <div>
            <h5 className="font-medium text-gray-900 mb-2">Response Time</h5>
            <p className="text-sm text-gray-600">
              We typically respond within 24 hours<br />
              Custom orders may take 2-3 business days
            </p>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <h5 className="font-medium text-gray-900 mb-2">What happens next?</h5>
          <ol className="text-sm text-gray-600 space-y-1">
            <li>1. We'll review your inquiry within 24 hours</li>
            <li>2. Our team will contact you to discuss details</li>
            <li>3. We'll provide quotes and timelines for your order</li>
            <li>4. Production begins once advance payment is received</li>
          </ol>
        </div>
      </div>
    </div>
  );

  const renderItemModal = () => (
    selectedItem && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xl font-bold text-gray-900">{selectedItem.modelName}</h3>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <img
                  src={selectedItem.photo}
                  alt={selectedItem.modelName}
                  className="w-full h-48 object-cover rounded-lg"
                />
              </div>

              <div className="space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Model Number</span>
                  <p className="font-semibold">{selectedItem.modelNumber}</p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">Size</span>
                  <p className="font-semibold">{selectedItem.size}</p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">Price</span>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedItem.price)}</p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">Stock Status</span>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="font-semibold">{selectedItem.currentStock} / {selectedItem.initialStock}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedItem.currentStock > selectedItem.initialStock * 0.5
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedItem.currentStock > selectedItem.initialStock * 0.5 ? 'In Stock' : 'Low Stock'}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="text-sm text-gray-500">Sales Performance</span>
                  <p className="font-semibold text-blue-600">{selectedItem.sold} units sold</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="EkDant Arts" className="w-10 h-10" />
              <h1 className="text-2xl font-bold text-gray-900">Product Demo</h1>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-gray-600">🧪 Demo Mode - Safe to Explore</span>
              {user && (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-700">Welcome, {user.name}</span>
                  <button
                    onClick={handleLogout}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              )}
              <button
                onClick={handleContinueToPricing}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center gap-2"
              >
                <FaCrown className="text-yellow-300" />
                View Pricing
                <FaArrowRight />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Explore Features</h2>

              <nav className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="text-lg" />
                      <div>
                        <div className="font-medium">{section.name}</div>
                        <div className="text-xs text-gray-500">{section.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* CTA in Sidebar */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={handleContinueToPricing}
                  className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 font-semibold shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <FaPlay className="text-green-300" />
                  Try Full Version
                </button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {activeSection === 'overview' && renderOverview()}
            {activeSection === 'shop' && renderShopDemo()}
            {activeSection === 'gallery' && renderGallery()}
            {activeSection === 'inventory' && renderInventory()}
            {activeSection === 'retail-orders' && renderRetailOrderForm()}
            {activeSection === 'wholesale-orders' && renderWholesaleOrderForm()}
            {activeSection === 'orders' && renderOrders()}
          </div>
        </div>

        {/* Public Shop Section */}
        <div className="mt-16">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-8 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">🛍️ Your Public Shop - Available to Everyone</h3>
            <p className="text-orange-100 mb-6 max-w-3xl mx-auto">
              Your online storefront is completely public and accessible to anyone with the URL.
              No login required - customers can browse, add to cart, and complete purchases instantly.
            </p>

            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="max-w-4xl mx-auto">
                <h4 className="text-xl font-semibold text-gray-900 mb-6">🌐 Public Shop Features</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">🛒</span>
                    </div>
                    <h5 className="font-semibold text-gray-900 mb-2">Retail Shopping</h5>
                    <p className="text-sm text-gray-600">Individual customers can buy single items with easy checkout</p>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">🚛</span>
                    </div>
                    <h5 className="font-semibold text-gray-900 mb-2">Wholesale Orders</h5>
                    <p className="text-sm text-gray-600">Bulk orders for businesses and resellers with flexible terms</p>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">📱</span>
                    </div>
                    <h5 className="font-semibold text-gray-900 mb-2">Mobile Friendly</h5>
                    <p className="text-sm text-gray-600">Fully responsive design works perfectly on all devices</p>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">⏰</span>
                    </div>
                    <h5 className="font-semibold text-gray-900 mb-2">24/7 Available</h5>
                    <p className="text-sm text-gray-600">Your shop works around the clock, generating sales anytime</p>
                  </div>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg border border-orange-200 mb-6">
                  <h5 className="text-lg font-semibold text-orange-900 mb-4">🎯 How Public Shop Orders Work</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-2xl mb-2">🛒</div>
                      <h6 className="font-medium text-gray-900 mb-1">Customer Places Order</h6>
                      <p className="text-sm text-gray-600">Anyone can visit your shop URL and place orders</p>
                    </div>

                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-2xl mb-2">📊</div>
                      <h6 className="font-medium text-gray-900 mb-1">Auto-Sync to Dashboard</h6>
                      <p className="text-sm text-gray-600">Orders appear instantly in your management system</p>
                    </div>

                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-2xl mb-2">🎯</div>
                      <h6 className="font-medium text-gray-900 mb-1">Process & Fulfill</h6>
                      <p className="text-sm text-gray-600">Handle production, shipping, and customer service</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={() => navigate('/demo-shop')}
                    className="bg-orange-600 text-white px-8 py-4 rounded-lg hover:bg-orange-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center gap-3 text-lg"
                  >
                    <span className="text-2xl">🛍️</span>
                    Experience Your Public Shop
                    <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Business?</h3>
          <p className="text-indigo-100 mb-6 max-w-2xl mx-auto">
            Experience all these features and more with a subscription. No setup fees, cancel anytime.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleContinueToPricing}
              className="bg-white text-indigo-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <FaCrown className="text-yellow-500" />
              View Subscription Plans
              <FaArrowRight />
            </button>

            <div className="flex items-center gap-2 text-indigo-200">
              <FaCheck className="text-green-400" />
              <span className="text-sm">30-day free trial available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Item Detail Modal */}
      {renderItemModal()}

      {/* Order Confirmation Modal */}
      {showOrderModal && orderConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">🎉 Order Confirmed!</h3>
                  <p className="text-gray-600">Your demo order has been successfully created</p>
                </div>
                <button
                  onClick={() => {
                    setShowOrderModal(false);
                    setOrderConfirmation(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Order Details */}
              <div className="bg-green-50 rounded-lg p-6 border border-green-200 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-900 mb-2">Order Information</h4>
                    <p className="text-sm text-gray-700"><strong>Order Number:</strong> {orderConfirmation.orderNumber}</p>
                    <p className="text-sm text-gray-700"><strong>Order Date:</strong> {orderConfirmation.orderDate}</p>
                    <p className="text-sm text-gray-700"><strong>Delivery Date:</strong> {orderConfirmation.deliveryDate}</p>
                    <p className="text-sm text-gray-700"><strong>Estimated Delivery:</strong> {orderConfirmation.estimatedDelivery}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-green-900 mb-2">Customer Details</h4>
                    <p className="text-sm text-gray-700"><strong>Name:</strong> {orderConfirmation.customerName}</p>
                    <p className="text-sm text-gray-700"><strong>Phone:</strong> {orderConfirmation.phone}</p>
                  </div>
                </div>
              </div>

              {/* Item Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-4">Order Items</h4>
                {orderConfirmation.type === 'wholesale' ? (
                  <div className="space-y-3">
                    {orderConfirmation.items.map((item, index) => (
                      <div key={index} className="flex items-center space-x-4 py-2 border-b border-gray-100 last:border-b-0">
                        <img src={item.model.photo} alt={item.model.modelName} className="w-12 h-12 object-cover rounded" />
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900">{item.model.modelName}</h5>
                          <p className="text-sm text-gray-600">
                            Model {item.model.modelNumber} • Qty: {item.quantity} • Advance: {formatCurrency(item.advance)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{formatCurrency(item.total)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center space-x-4">
                    <img src={orderConfirmation.item.photo} alt={orderConfirmation.item.modelName} className="w-16 h-16 object-cover rounded" />
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">{orderConfirmation.item.modelName}</h5>
                      <p className="text-sm text-gray-600">Model {orderConfirmation.item.modelNumber} • {orderConfirmation.item.size}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{formatCurrency(orderConfirmation.totalAmount)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Summary */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-6">
                <h4 className="font-semibold text-blue-900 mb-3">Payment Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700">Total Amount:</span>
                    <span className="font-semibold">{formatCurrency(orderConfirmation.totalAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700">Advance Paid:</span>
                    <span className="font-semibold text-green-600">{formatCurrency(orderConfirmation.advancePaid)}</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-300 pt-2">
                    <span className="text-sm font-medium text-gray-900">Balance Due:</span>
                    <span className="font-bold text-red-600">{formatCurrency(orderConfirmation.balanceDue)}</span>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mb-6">
                <h4 className="font-semibold text-yellow-900 mb-3">📋 What Happens Next?</h4>
                <ol className="text-sm text-gray-700 space-y-1">
                  <li>1. Production begins once advance payment is received</li>
                  <li>2. You'll receive updates on production progress</li>
                  <li>3. Item will be ready by {orderConfirmation.estimatedDelivery}</li>
                  <li>4. Balance payment due upon delivery</li>
                </ol>
              </div>

              {/* Demo Notice */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-800">
                  🎭 <strong>Demo Order:</strong> This is a demonstration order. No real production or delivery will occur.
                  Your order details have been saved locally for demo purposes.
                </p>
              </div>

              {/* Close Button */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => {
                    setShowOrderModal(false);
                    setOrderConfirmation(null);
                  }}
                  className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  Close & Continue Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoPage;
